import { AuthService } from './authService';
import { 
  SupportedLanguage, 
  LanguageResource, 
  TranslationCache, 
  LANGUAGE_CONFIG 
} from '../i18n';

// DTOs matching backend
export interface TranslationOverrideDto {
  id?: number;
  languageCode: string;
  translationKey: string;
  translationValue: string;
  createdAt?: string;
  modifiedAt?: string;
}

export interface SaveTranslationOverrideRequest {
  languageCode: string;
  translationKey: string;
  translationValue: string;
}

export interface BulkTranslationSaveRequest {
  translations: SaveTranslationOverrideRequest[];
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

export class TranslationService {
  /**
   * Load default translations from JSON files
   */
  static async loadDefaultTranslations(language: SupportedLanguage): Promise<LanguageResource> {
    try {
      // Import the JSON file dynamically
      const translations = await import(`../locales/${language}/common.json`);
      // The JSON file already has the correct structure with namespaces
      return translations.default || translations;
    } catch (error) {
      console.error(`Failed to load default translations for ${language}:`, error);
      return {};
    }
  }

  /**
   * Load custom translation overrides from backend
   */
  static async loadCustomTranslations(
    companyNo: string, 
    language: SupportedLanguage
  ): Promise<Record<string, string>> {
    try {
      const response = await AuthService.authenticatedGet(
        `${API_BASE_URL}/${companyNo}/translation/overrides/${language}`
      );

      if (!response.ok) {
        if (response.status === 404) {
          // No custom translations found - this is OK
          return {};
        }
        throw new Error(`Failed to load custom translations: ${response.status}`);
      }

      const overrides: TranslationOverrideDto[] = await response.json();
      
      // Convert array of overrides to flat key-value object
      return overrides.reduce((acc, override) => {
        acc[override.translationKey] = override.translationValue;
        return acc;
      }, {} as Record<string, string>);
      
    } catch (error) {
      console.warn(`Failed to load custom translations for ${language}:`, error);
      return {};
    }
  }

  /**
   * Merge default translations with custom overrides
   */
  static mergeTranslations(
    defaultTranslations: LanguageResource,
    customOverrides: Record<string, string>
  ): LanguageResource {
    const merged = JSON.parse(JSON.stringify(defaultTranslations)); // Deep clone

    // Apply custom overrides
    Object.entries(customOverrides).forEach(([key, value]) => {
      this.setNestedValue(merged, key, value);
    });

    return merged;
  }

  /**
   * Set nested value in object using dot notation
   */
  private static setNestedValue(obj: any, path: string, value: any): void {
    const keys = path.split('.');
    const lastKey = keys.pop()!;
    const target = keys.reduce((current, key) => {
      if (current[key] === undefined) {
        current[key] = {};
      }
      return current[key];
    }, obj);
    target[lastKey] = value;
  }

  /**
   * Load complete translations for language (default + custom)
   */
  static async loadTranslations(
    companyNo: string, 
    language: SupportedLanguage,
    useCache: boolean = true
  ): Promise<LanguageResource> {
    // Try cache first
    if (useCache) {
      const cached = TranslationCache.getCachedTranslations(language);
      if (cached) {
        return cached;
      }
    }

    // Load both default and custom translations in parallel
    const [defaultTranslations, customOverrides] = await Promise.all([
      this.loadDefaultTranslations(language),
      this.loadCustomTranslations(companyNo, language)
    ]);

    // Merge translations
    const mergedTranslations = this.mergeTranslations(defaultTranslations, customOverrides);

    // Cache the result
    TranslationCache.cacheTranslations(language, mergedTranslations);

    return mergedTranslations;
  }

  /**
   * Save custom translation override
   */
  static async saveTranslationOverride(
    companyNo: string,
    request: SaveTranslationOverrideRequest
  ): Promise<void> {
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/${companyNo}/translation/override`,
      request
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to save translation override');
    }

    // Clear cache to force reload
    TranslationCache.clearCache();
  }

  /**
   * Save multiple translation overrides
   */
  static async saveBulkTranslationOverrides(
    companyNo: string,
    request: BulkTranslationSaveRequest
  ): Promise<void> {
    const response = await AuthService.authenticatedPost(
      `${API_BASE_URL}/${companyNo}/translation/overrides/bulk`,
      request
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to save translation overrides');
    }

    // Clear cache to force reload
    TranslationCache.clearCache();
  }

  /**
   * Delete translation override
   */
  static async deleteTranslationOverride(
    companyNo: string,
    languageCode: string,
    translationKey: string
  ): Promise<void> {
    const response = await AuthService.authenticatedDelete(
      `${API_BASE_URL}/${companyNo}/translation/override/${languageCode}/${encodeURIComponent(translationKey)}`
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to delete translation override');
    }

    // Clear cache to force reload
    TranslationCache.clearCache();
  }

  /**
   * Get all translation overrides for a language
   */
  static async getTranslationOverrides(
    companyNo: string,
    language: SupportedLanguage
  ): Promise<TranslationOverrideDto[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/${companyNo}/translation/overrides/${language}`
    );

    if (!response.ok) {
      if (response.status === 404) {
        return [];
      }
      const error = await response.json();
      throw new Error(error.message || 'Failed to get translation overrides');
    }

    return await response.json();
  }

  /**
   * Get all translation overrides for all languages
   */
  static async getAllTranslationOverrides(
    companyNo: string
  ): Promise<TranslationOverrideDto[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/${companyNo}/translation/overrides`
    );

    if (!response.ok) {
      if (response.status === 404) {
        return [];
      }
      const error = await response.json();
      throw new Error(error.message || 'Failed to get translation overrides');
    }

    return await response.json();
  }

  /**
   * Import translations from file (JSON format)
   */
  static async importTranslations(
    companyNo: string,
    language: SupportedLanguage,
    file: File
  ): Promise<void> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('languageCode', language);

    const response = await AuthService.authenticatedFetch(
      `${API_BASE_URL}/${companyNo}/translation/import`,
      {
        method: 'POST',
        body: formData,
        headers: {
          // Remove Content-Type to let browser set it with boundary for FormData
          'Authorization': `Bearer ${AuthService.getToken()}`
        }
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to import translations');
    }

    // Clear cache to force reload
    TranslationCache.clearCache();
  }

  /**
   * Export translations to JSON
   */
  static async exportTranslations(
    companyNo: string,
    language: SupportedLanguage,
    includeDefaults: boolean = false
  ): Promise<Blob> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/${companyNo}/translation/export/${language}?includeDefaults=${includeDefaults}`
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to export translations');
    }

    return await response.blob();
  }

  /**
   * Reset translations to defaults (delete all custom overrides)
   */
  static async resetTranslations(
    companyNo: string,
    language?: SupportedLanguage
  ): Promise<void> {
    const url = language 
      ? `${API_BASE_URL}/${companyNo}/translation/reset/${language}`
      : `${API_BASE_URL}/${companyNo}/translation/reset`;

    const response = await AuthService.authenticatedDelete(url);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to reset translations');
    }

    // Clear cache to force reload
    TranslationCache.clearCache();
  }

  /**
   * Get translation statistics
   */
  static async getTranslationStats(companyNo: string): Promise<{
    totalKeys: number;
    customOverrides: number;
    languages: Record<SupportedLanguage, { overrides: number; coverage: number }>;
  }> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/${companyNo}/translation/stats`
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to get translation stats');
    }

    return await response.json();
  }
}